import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Calendar, User, Tag, ArrowLeft } from "lucide-react";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-8 h-10 w-3/4 animate-pulse rounded-lg bg-muted/30" />
        <div className="mb-6 h-96 w-full animate-pulse rounded-xl bg-muted/30" />
        <div className="space-y-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} data-index={i} className="h-4 animate-pulse rounded bg-muted/30" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12 text-center sm:px-6 lg:px-8">
        <p className="text-muted-foreground mb-4">Fehler beim Laden des Beitrags.</p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Erneut versuchen
        </Button>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-12 text-center sm:px-6 lg:px-8">
        <p className="text-muted-foreground">Beitrag nicht gefunden.</p>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
      <Link
        to="/"
        className="mb-8 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Zurück zur Übersicht
      </Link>

      <header className="mb-8">
        <h1 className="mb-6 text-4xl font-bold leading-tight text-foreground lg:text-5xl">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          {post.author && (
            <div className="flex items-center gap-2">
              <User className="size-4" />
              <span>{post.author.name}</span>
            </div>
          )}

          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString("de-DE", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </time>
            </div>
          )}

          {post.categories && post.categories.length > 0 && (
            <div className="flex items-center gap-2">
              <Tag className="size-4" />
              <div className="flex flex-wrap gap-2">
                {post.categories.map((category, i) => (
                  <span key={category.id} data-index={i} className="text-primary">
                    {category.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </header>

      {post.featuredImage && (
        <div className="mb-8 overflow-hidden rounded-xl">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="h-auto w-full object-cover"
          />
        </div>
      )}

      <Separator className="my-8" />

      <div
        className="prose prose-lg max-w-none prose-headings:font-display prose-headings:font-semibold prose-headings:text-foreground prose-p:text-foreground prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground prose-img:rounded-xl"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      {post.tags && post.tags.length > 0 && (
        <div className="mt-12">
          <Separator className="mb-6" />
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-muted-foreground">Schlagwörter:</span>
            {post.tags.map((tag, i) => (
              <span
                key={tag.id}
                data-index={i}
                className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-1 text-sm text-primary transition-colors hover:border-primary/40 hover:bg-primary/10"
              >
                {tag.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {post.author && (
        <div className="mt-12">
          <Separator className="mb-6" />
          <div className="flex items-start gap-4 rounded-xl border border-muted/30 bg-muted-light p-6">
            <Avatar className="size-16">
              {post.author.avatar && post.author.avatar["96"] && (
                <AvatarImage src={post.author.avatar["96"]} alt={post.author.name} />
              )}
              <AvatarFallback className="bg-primary/10 text-primary">
                {post.author.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()
                  .slice(0, 2)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="mb-1 text-lg font-semibold text-foreground">
                {post.author.name}
              </h3>
              <p className="text-sm text-muted-foreground">Autor</p>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}

export default function SinglePostTemplate() {
  const currentPostId = wvcClient.getCurrentPostID();

  return (
    <PostsProvider wp_query={{ include: [currentPostId], per_page: 1 }} embeds={["wp:featuredmedia", "wp:term", "author"]} fields={["id", "title.rendered", "content.rendered", "date", "author", "categories", "tags", "link", "_embedded", "featured_media", "_links.wp:featuredmedia", "_links.wp:term", "_links.author"]}>
      <SinglePostContent />
    </PostsProvider>
  );
}